import { useEffect, useState } from 'react';

// Pacer de reveal côté affichage (même architecture que le RevealEngine iOS 30 Hz
// et le plan pacer Android) : le texte reçu est bufferisé et n'entre dans le DOM
// qu'à cadence bornée et MONOTONE. C'est ce qui garantit l'ordre visuel : un
// stagger CSS ne peut pas le faire (délai = index × stagger depuis le mount ⇒
// la queue d'un batch apparaît après la tête du batch suivant dès que le débit
// dépasse tick/stagger — inversions « texte à trous » constatées en QA).
const TICK_MS = 33; // ~30 Hz, aligné sur l'existant iOS
// Résorbe le backlog en ~10 ticks (~330 ms) : assez lent pour lisser les rafales
// SSE du H100, assez rapide pour ne jamais sembler à la traîne.
const CATCH_UP_TICKS = 10;
const MIN_CHARS_PER_TICK = 4;
const MAX_CHARS_PER_TICK = 160;
// Drain accéléré une fois le stream terminé : on finit la lecture, vite.
const DRAIN_MULTIPLIER = 4;

export function computeNextRevealLength(
  revealedLength: number,
  totalLength: number,
  isStreaming: boolean,
) {
  if (totalLength <= revealedLength) {
    // Texte raccourci (reset/regénération) : resynchronisation immédiate.
    return totalLength;
  }
  const backlog = totalLength - revealedLength;
  const baseStep = Math.min(
    MAX_CHARS_PER_TICK,
    Math.max(MIN_CHARS_PER_TICK, Math.ceil(backlog / CATCH_UP_TICKS)),
  );
  const step = isStreaming ? baseStep : baseStep * DRAIN_MULTIPLIER;
  return Math.min(totalLength, revealedLength + step);
}

export function useStreamRevealText(fullText: string, isStreaming: boolean) {
  // Un message d'historique (jamais streamé) est révélé d'emblée : aucun pacer,
  // aucun re-render — le coût est strictement limité au message en cours.
  const [revealedLength, setRevealedLength] = useState(
    isStreaming ? 0 : fullText.length,
  );
  // Latch « ce message a streamé » via ajustement d'état en render (pattern
  // React documenté) : le drain du pacer doit survivre à isStreaming → false.
  const [hasStreamed, setHasStreamed] = useState(isStreaming);
  if (isStreaming && !hasStreamed) {
    setHasStreamed(true);
  }

  const isRevealing = hasStreamed && revealedLength < fullText.length;

  useEffect(() => {
    if (!isRevealing) {
      return;
    }

    let rafId = 0;
    let lastTickMs = performance.now();
    const tick = (nowMs: number) => {
      if (nowMs - lastTickMs >= TICK_MS) {
        lastTickMs = nowMs;
        setRevealedLength((current) =>
          computeNextRevealLength(current, fullText.length, isStreaming));
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafId);
  }, [fullText.length, isRevealing, isStreaming]);

  // Resynchronisation si le texte a raccourci (reset) hors boucle de reveal.
  const boundedLength = Math.min(revealedLength, fullText.length);

  return {
    isRevealing,
    text: hasStreamed ? fullText.slice(0, boundedLength) : fullText,
  };
}
